// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, useState, type ComponentProps } from 'react';

import {
  type StrongOmit,
  type Part,
  type PartListPart,
  type CreateEditPartBody,
  type Manufacturer,
  type UnitOfMeasure,
  type PartAttributeDefinitionId,
  type PartAttributeDefinition,
  type PartCatalog,
  PART_TYPE_ASSEMBLY_ID,
  type PartAttributeDefinitionVariantId,
} from '@evolvemep/api-ts';
import { Button, Divider, Flex, Text, Stack, LoadingOverlay, TextInput } from '@mantine/core';
import type { FormStatus } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { ImageUpload } from 'components/Mantine/FileUploads/ImageUpload';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { TextareaDebounced, TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { ManufacturerSelect } from 'hooks/typeCacheHooks/ManufacturerSelect';
import { UnitOfMeasureSelect } from 'hooks/typeCacheHooks/UnitOfMeasureSelect';
import { AddAttributePopover } from 'modules/Shop/CatalogSetup/AddAttributePopover';
import { AttributeWithVariants } from 'modules/Shop/CatalogSetup/AttributeWithVariants';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import { ExistingCategorySelector } from 'modules/Shop/CatalogSetup/ExistingCategorySelector';
import { PART_NAME_MAX_LENGTH } from 'modules/Shop/CatalogSetup/PartDetail';
import { usePartEditor, type CreateEditPartFormType } from 'modules/Shop/CatalogSetup/PartEditor/usePartEditor';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

export type CreateEditPartFormProps = {
  partCategory?: CategoryPath[number];
  onPartUpdated?: (part: Part) => void;
  part?: PartListPart;
  disabled?: boolean;
  includeAssemblyButton?: boolean;
  onClose?: () => void;
};

export type CreateEditPartFormBody = StrongOmit<
  CreateEditPartBody,
  'partCategoryId' | 'manufacturerId' | 'unitOfMeasureId' | 'image'
> & {
  image?: File;
  partCategory: CategoryPath[number];
  manufacturer: StrongOmit<Manufacturer, 'editable'>;
  unitOfMeasure: UnitOfMeasure;
};

export type MoveToAssemblyEditorState = {
  part?: Part;
  formValues?: CreateEditPartFormBody;
  isDirty?: FormStatus; // Record<keyof CreateEditPartFormBody, boolean>;
  cachedAttributes?: Record<PartAttributeDefinitionId, PartAttributeDefinition>;
  partCatalog: PartCatalog;
};

export const CreateEditPartFields = ({
  onClose,
  includeAssemblyButton,
  disabled,
  duplicatePart,
}: StrongOmit<CreateEditPartFormProps, 'part'> & {
  duplicatePart?: boolean;
}) => {
  const navigate = useEvolveNavigate('Catalog Setup');
  const { part, form, publishing, partCatalog, cachedAttributes, onAttributeLoaded } = usePartEditor();

  const { unblock } = useBlockerWhen('part-editor', form.isDirty());

  const sortedAttributes = useMemo(
    () =>
      [
        ...form.values.globalPartAttributes.map((a, index) => ({
          ...a,
          // Store the original `index` so we can index back into `globalPartAttributes`
          index,
        })),
        ...form.values.localPartAttributes.map((b, index) => ({
          ...b,
          // Store the original `index` so we can index back into `localPartAttributes`
          index,
        })),
      ].toSorted((a, b) => {
        const aName =
          'partAttributeDefinitionId' in a
            ? (cachedAttributes[a.partAttributeDefinitionId]?.name ?? a.partAttributeDefinitionId)
            : a.name;
        const bName =
          'partAttributeDefinitionId' in b
            ? (cachedAttributes[b.partAttributeDefinitionId]?.name ?? b.partAttributeDefinitionId)
            : b.name;
        return aName.localeCompare(bName);
      }),
    [cachedAttributes, form.values.globalPartAttributes, form.values.localPartAttributes],
  );

  const [selectedPartCatalog, setSelectedPartCatalog] = useState<PartCatalog | null>(
    partCatalog.editable ? partCatalog : null,
  );

  return (
    <>
      <ImageUpload disabled={publishing} existingFile={part?.image?.fileId} {...form.getInputProps('image')} />

      <TextInputDebounced
        label={`${duplicatePart ? 'New ' : ''}${part?.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Assembly Name' : 'Part Name'}`}
        autoFocus={!form.values.name || duplicatePart}
        data-autofocus={!form.values.name || duplicatePart ? 'true' : undefined}
        required
        leading
        maxLength={PART_NAME_MAX_LENGTH}
        {...form.getInputProps('name')}
      />
      <TextareaDebounced label="Description" leading maxLength={255} {...form.getInputProps('description')} />

      {duplicatePart && (
        <CatalogSelect
          showLabel
          required
          selectedCatalog={selectedPartCatalog}
          onCatalogSelected={(c) => {
            setSelectedPartCatalog(c);
            form.setFieldValue('partCategory', null);
          }}
          onlyEditable
          autoselect={false}
          disabled={disabled}
        />
      )}

      <ExistingCategorySelector
        placeholder=""
        required
        partCatalog={selectedPartCatalog}
        {...form.getInputProps('partCategory')}
      />
      <ManufacturerSelect required {...form.getInputProps('manufacturer')} disabled={duplicatePart} />
      <TextInput label="Catalog #" maxLength={64} {...form.getInputProps('manufacturerCatalogNumber')} />
      {
        // These fields cannot be changed when duplicating a part
        !duplicatePart && (
          <>
            <UnitOfMeasureSelect required {...form.getInputProps('unitOfMeasure')} />

            {includeAssemblyButton && (
              <Button
                flex="0 0 auto"
                size="compact-sm"
                variant="gradient"
                leftSection={<EvolveIcon icon="Assembly" size="sm" />}
                disabled={publishing || disabled}
                onClick={() => {
                  unblock?.();
                  onClose?.();
                  void navigate(
                    isNotNil(part)
                      ? `/edit-catalog-item/${part.partId}`
                      : `/add-catalog-item/${partCatalog.partCatalogId}`,
                    {
                      additionalData: {
                        part,
                        formValues: form.values,
                        isDirty: form.getDirty(),
                        cachedAttributes,
                        partCatalog,
                      } as MoveToAssemblyEditorState,
                    },
                  );
                }}
              >
                {part?.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Edit' : 'Create'} assembly
              </Button>
            )}

            <Flex justify="space-between" gap="xs" align="center" mt="xs">
              <Divider label="Attributes" style={{ flex: '1 1 auto' }} />
              <AddAttributePopover
                attributesSelected={form.values.globalPartAttributes.map((a) => a.partAttributeDefinitionId)}
                onAttributeSelected={(attribute, globalAttribute) => {
                  onAttributeLoaded(globalAttribute);
                  form.insertListItem('globalPartAttributes', attribute);
                }}
                onCustomAttributeCreated={(attribute) => {
                  form.insertListItem('localPartAttributes', attribute);
                }}
                disabled={publishing || disabled}
              />
            </Flex>

            {sortedAttributes.length === 0 && (
              <Text c="dimmed" fz="xs">
                No part attributes.
              </Text>
            )}

            {sortedAttributes.map(({ index, ...attribute }) => {
              if ('partAttributeDefinitionId' in attribute) {
                const globalAttribute =
                  cachedAttributes[attribute.partAttributeDefinitionId] ??
                  part?.globalPartAttributes.find(
                    (a) => a.partAttributeDefinitionId === attribute.partAttributeDefinitionId,
                  );
                if (isNil(globalAttribute)) return null;
                return (
                  <AttributeWithVariants
                    key={attribute.partAttributeDefinitionId}
                    name={globalAttribute.name}
                    disabled={publishing || disabled}
                    allowWriteInVariant={globalAttribute.allowWriteInVariant}
                    editable
                    partAttributeDefinition={globalAttribute}
                    onPartAttributeDefinitionUpdated={onAttributeLoaded}
                    value={form.values.globalPartAttributes[index]?.partAttributeDefinitionVariantIds}
                    onChange={(v: PartAttributeDefinitionVariantId[]) =>
                      form.setFieldValue(`globalPartAttributes.${index}.partAttributeDefinitionVariantIds`, v)
                    }
                    onRemove={() => form.removeListItem('globalPartAttributes', index)}
                  />
                );
              }
              return (
                <AttributeWithVariants
                  key={attribute.name}
                  name={attribute.name}
                  disabled={publishing || disabled}
                  editable
                  allowWriteInVariant={attribute.allowWriteInVariant}
                  value={form.values.localPartAttributes[index]?.variants}
                  onChange={(v: string[]) => form.setFieldValue(`localPartAttributes.${index}.variants`, v)}
                  onRemove={() => form.removeListItem('localPartAttributes', index)}
                />
              );
            })}
          </>
        )
      }
    </>
  );
};

export const CreateEditPartForm = (props: ComponentProps<typeof CreateEditPartFields>) => {
  const { publishing, publishPart, form } = usePartEditor();
  const { isDirty } = form;
  const onSubmit = async (formValues: CreateEditPartFormType) => {
    const part = await publishPart({ duplicateAsNew: props.duplicatePart, formValues });
    props.onPartUpdated?.(part);
    props.onClose?.();
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)} style={{ overflow: 'hidden' }}>
      <Stack h="100%" style={{ overflow: 'hidden' }}>
        {!!props.onClose && (
          <Flex gap="md" justify="flex-end">
            <Button variant="light" onClick={props.onClose}>
              Cancel edit
            </Button>
            <Button type="submit" disabled={!isDirty}>
              Save
            </Button>
          </Flex>
        )}
        <Stack flex={1} style={{ overflowY: 'auto' }} mt="md">
          <CreateEditPartFields {...props} />
        </Stack>
      </Stack>
      <LoadingOverlay visible={publishing} />
    </form>
  );
};
